/**
 * units.ts — wind-speed unit selection.
 *
 * The display unit defaults from the selected location's COUNTRY (quick lookup
 * table below). If the user clicks the unit to toggle it, that choice is kept
 * for 20 minutes; after it expires the unit auto-picks from location again.
 * With no location selected (simulated wind) the default comes from the
 * browser locale's region — a country-level signal that needs no network or
 * permission. (True IP geolocation would need a server lookup; the Cloudflare
 * Worker gateway could expose request.cf.country later — same table applies.)
 */

export type WindUnit = 'kmh' | 'mph';

/** Countries whose common wind/road speed unit is mph (everywhere else: km/h).
 *  Keyed by the English country names the geocoder returns. */
const MPH_COUNTRIES = new Set([
  'United States',
  'United Kingdom',
  'Myanmar',
  'Liberia',
  // UK crown dependencies & overseas territories
  'Isle of Man',
  'Jersey',
  'Guernsey',
  'Gibraltar',
  'Bermuda',
  'Falkland Islands',
  'Saint Helena',
  'Anguilla',
  'Montserrat',
  'British Virgin Islands',
  'Cayman Islands',
  'Turks and Caicos Islands',
  // US territories
  'Puerto Rico',
  'Guam',
  'American Samoa',
  'U.S. Virgin Islands',
  'United States Virgin Islands',
  'Northern Mariana Islands',
  // Caribbean & Pacific mph holdouts
  'Antigua and Barbuda',
  'Bahamas',
  'Belize',
  'Dominica',
  'Grenada',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'Marshall Islands',
  'Micronesia',
  'Federated States of Micronesia',
  'Palau',
]);

/** Same set, keyed by ISO 3166-1 alpha-2 region codes (for the browser-locale path). */
const MPH_REGIONS = new Set([
  'US', 'GB', 'MM', 'LR',
  'IM', 'JE', 'GG', 'GI', 'BM', 'FK', 'SH', 'AI', 'MS', 'VG', 'KY', 'TC',
  'PR', 'GU', 'AS', 'VI', 'MP',
  'AG', 'BS', 'BZ', 'DM', 'GD', 'KN', 'LC', 'VC', 'WS', 'MH', 'FM', 'PW',
]);

/** Country-level region from the browser locale (e.g. "en-US" → "US"). */
function localeRegion(): string | undefined {
  try {
    const loc = new Intl.Locale(navigator.language);
    return loc.region ?? loc.maximize().region;
  } catch {
    return undefined;
  }
}

/** The auto-picked unit: selected country when live, browser locale when simulated. */
export function autoUnit(country?: string): WindUnit {
  if (country) return MPH_COUNTRIES.has(country) ? 'mph' : 'kmh';
  const region = localeRegion();
  return region && MPH_REGIONS.has(region) ? 'mph' : 'kmh';
}

// --- 20-minute manual override ---------------------------------------------

const OVERRIDE_KEY = 'windUnitOverride';
const OVERRIDE_TTL_MS = 20 * 60 * 1000;

export function setUnitOverride(unit: WindUnit): void {
  try {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify({ unit, exp: Date.now() + OVERRIDE_TTL_MS }));
  } catch {
    /* storage unavailable (private mode) — the toggle simply won't persist */
  }
}

/** The user's clicked unit, if it hasn't expired yet; null → auto-pick. */
export function unitOverride(): WindUnit | null {
  try {
    const raw = localStorage.getItem(OVERRIDE_KEY);
    if (!raw) return null;
    const { unit, exp } = JSON.parse(raw) as { unit?: string; exp?: number };
    if (typeof exp !== 'number' || Date.now() > exp) {
      localStorage.removeItem(OVERRIDE_KEY);
      return null;
    }
    return unit === 'mph' ? 'mph' : 'kmh';
  } catch {
    return null;
  }
}
